import _ from "lodash";
import React, { useState } from "react";
import styled from "styled-components";

import { Icon, MenuItem, Tooltip } from "@material-ui/core";
import { IWorkspaceResourcePresentationTemplate } from "common/interfaces";
import WorkspaceController from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import { getPresentation } from "js/core/models/presentation";
import getUserProfile from "js/core/services/userProfiles";
import { app } from "js/namespaces";
import { ShowConfirmationDialog, ShowDialog, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import { FlexSpacer, Gap10, Gap20, Gap5 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import LoadableAsync from "js/react/components/LoadableAsync";
import { IconMenu } from "js/react/components/UiComponents";
import { themeColors } from "js/react/sharedStyles";
import { EmptySlideThumbnail, SlideThumbnail } from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid";
import TeamTemplateSettingsDialog from "js/react/views/TeamResources/dialogs/TeamTemplateSettingsDialog";

import { IUpdateWorkspacesMetadata, PresentationTemplateItemProps } from "./types";
import { t } from "js/i18n";
import UpgradeIcon from "js/Components/UpgradeIcon";

const StyledTemplateItem = styled.div`
    width: 100%;
    margin-bottom: 20px;
    background: white;
    padding: 10px 20px 10px 20px;
    border: solid 1px white;

    &:hover {
        border: solid 1px ${themeColors.ui_blue};
    }

    &.selected {
        background: ${themeColors.lightBlue};
    }

    &.hidden {
        display: none;
    }

    .title {
        font-weight: 600;
        font-size: 16px;
    }
`;

const SlideThumbnailsContainer = styled.div`
    width: 100%;
    height: 142px;
`;

const SlideThumbnails = styled.div`
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: 200px;
    width: 100%;
    overflow-x: auto;
    gap: 10px;
    padding: 10px 1px;
`;

const PublishLabel = styled.div`
    padding: 5px 10px;
    color: white;
    background: ${props => props.published ? themeColors.ui_blue : "orangered"};
    border-radius: 2px;
    text-transform: uppercase;
    font-size: 12px;
`;

const Stat = styled.div`
    display: flex;
    align-items: center;
    color: rgba(102, 102, 102, ${props => props.active ? 1 : 0.5});

    &.ratedBy {
        cursor: pointer;

        .MuiIcon-root {
            color: ${props => props.liked ? themeColors.yellow : "inherit"};
        }
    }

    &:hover {
        color: ${themeColors.ui_blue};

        .MuiIcon-root {
            color: ${themeColors.ui_blue};
        }
    }
`;

const StyledCreatedByInfo = styled.div`
    padding: 10px;
`;

const updateWorkspaceResourceMetadata =
    async ({ resourceId, resourceType }: IWorkspaceResourcePresentationTemplate,
        metadata: IUpdateWorkspacesMetadata)=>{
        await WorkspaceController.updateWorkspaceResource({
            resourceId: resourceId,
            resourceType: resourceType,
            metadata
        });
    };

export const PresentationTemplateItem = function(props: PresentationTemplateItemProps) {
    const {
        templateResource,
        editable,
        existingTags,
        selected,
        onConfirm,
        onSelection,
        visible,
        canUpgradeToManageSharedResources
    } = props;
    const baseTemplateItem = {
        id: templateResource.resourceId, // For PresentationTemplates, resourceId should always be equal to templateResource.id
        description: templateResource.metadata.description,
        name: templateResource.metadata.name,
        isPublished: templateResource.metadata.isPublished,
        ratedByUsers: templateResource.metadata.ratedByUsers,
        ratedByCount: Object.keys(templateResource.metadata.ratedByUsers).length,
        slideCount: templateResource.metadata.slideCount,
        tags: templateResource.tags
    };

    const [loadedState, setLoadedState] = useState({ createdByName: "" });
    const [error, setError] = useState(null);

    const fetchTemplateItem = async () => {
        const { resourceId } = templateResource;

        try {
            const template = await getPresentation(resourceId);
            let {
                createdById,
                modifiedById,
                userId
            } = template.toJSON();

            const profile = await getUserProfile(createdById ?? userId);

            const _loadedState = {
                createdById: profile.id,
                createdByName: profile.displayName || profile.email || "Unknown",
                modifiedById: modifiedById ?? userId,
                template,
                sips: template.getSips()
            };

            setLoadedState(_loadedState);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDoubleClick = () => {
        onSelection && onSelection({ ...baseTemplateItem, ...loadedState });
        if (!canUpgradeToManageSharedResources) {
            onConfirm && onConfirm({ ...baseTemplateItem, ...loadedState });
        }
    };

    const handleDeleteItem = async item => {
        if (await ShowConfirmationDialog({
            title: t("Are you sure you want to delete this template?"),
            message: t("This action can not be undone.")
        })) {
            await WorkspaceController.deleteWorkspaceResource({
                resourceId: templateResource.resourceId,
                resourceType: templateResource.resourceType,
            });
        }
    };

    const handleEditItem = () => {
        AppController.showEditor({ presentationId: templateResource.resourceId });
    };

    const handleUpdateTemplateSettings = async updatedSettings => {
        if (!_.isEmpty(updatedSettings)) {
            try {
                const metadata = _.pickBy({
                    description: updatedSettings.description,
                    isPublished: updatedSettings.isPublished,
                    name: updatedSettings.name
                }, value => value !== undefined);

                const tags = updatedSettings.tags;

                const updates = {
                    resourceId: templateResource.resourceId,
                    resourceType: templateResource.resourceType,
                    ...(!_.isEmpty(metadata) && { metadata }),
                    ...(tags && { tags })
                };

                await WorkspaceController.updateWorkspaceResource(updates);
            } catch (err) {
                ShowErrorDialog({
                    title: t("Unable to edit presentation settings"),
                    message: err.message
                });
            }
        }
    };

    const handleEditTemplateSettings = () => {
        ShowDialog(TeamTemplateSettingsDialog, {
            item: { ...baseTemplateItem, ...loadedState },
            existingTags,
            editTemplate: handleEditItem,
            updateTemplate: handleUpdateTemplateSettings
        });
    };

    const handlePublish = async () => {
        if (await ShowConfirmationDialog({
            title: t("Are you sure you want to publish this Presentation Template to your team?"),
            message: t("Your team members will be able to create a new presentation starting from this template by selecting it in Create New Presentation dialog.")
        })) {
            await updateWorkspaceResourceMetadata(templateResource, { isPublished: true });
        }
    };

    const handleUnpublish = async () => {
        await updateWorkspaceResourceMetadata(templateResource, { isPublished: false });
    };

    const handleToggleLike = async () => {
        const { ratedByUsers } = baseTemplateItem;
        const isLikedByCurrentUser = ratedByUsers.includes(app.user.id)
            ? { remove: [app.user.id] }
            : { add: [app.user.id] };

        await updateWorkspaceResourceMetadata(templateResource, { ratedByUsers: isLikedByCurrentUser });
    };

    // We hide non-matching results using CSS to avoid rerender / reload when visibility changes
    const cssClass = (!visible
        ? "hidden"
        : (!editable && selected)
            ? "selected"
            : "");

    const {
        description,
        name,
        isPublished,
        ratedByUsers,
        slideCount
    } = baseTemplateItem;

    const ratedByCount = Object.keys(ratedByUsers).length;
    const likedByUser = ratedByUsers.includes(app.user.id);
    const favoriteTooltip = likedByUser
        ? t("Click to unfavorite this template.")
        : t("Click to favorite this template.");

    return (<StyledTemplateItem
        key={baseTemplateItem.id}
        className={cssClass}
        onClick={() => onSelection({ ...baseTemplateItem, loadedState })}
        onDoubleClick={handleDoubleClick}
        readOnly={!editable}
    >
        <FlexBox left center fillHeight>
            <div className="title">{name}</div>
            <Gap20/>
            {editable && isPublished && <PublishLabel published>{t("Published")}</PublishLabel>}
            {editable && !isPublished && <PublishLabel onClick={handlePublish}>{t("Draft")}</PublishLabel>}
            <FlexSpacer/>
            <FlexBox center onDoubleClick={e => e.stopPropagation()}>
                <Tooltip
                    title={(
                        <div style={{ padding: 10 }}>
                            {favoriteTooltip}
                        </div>
                    )}
                    classes={{ popper: "popper-white rounded-5" }}
                    placement="top"
                    arrow
                    interactive
                >
                    <Stat
                        active={ratedByCount}
                        liked={likedByUser}
                        className="ratedBy"
                        onClick={handleToggleLike}
                    >
                        <Icon>star</Icon>
                        <span>{ratedByCount}</span>
                    </Stat>
                </Tooltip>
                <Gap5/>
                <Tooltip
                    title={
                        (<StyledCreatedByInfo>
                            {description && (<>
                                <div>{description}</div>
                                <Gap10/>
                            </>)}
                            <div>{t("Created by")} <strong>{loadedState.createdByName}</strong></div>
                        </StyledCreatedByInfo>)
                    }
                    classes={{ popper: "popper-white rounded-5" }}
                    placement="top"
                    arrow
                    interactive
                >
                    <Stat active={description}>
                        <Icon className="stat">info</Icon>
                    </Stat>
                </Tooltip>
                {
                    editable &&
                    <IconMenu>
                        {canUpgradeToManageSharedResources ? [
                            <MenuItem key="settings" disabled divider><UpgradeIcon/><Gap5/>{t("Template Settings...")}</MenuItem>,
                            <MenuItem key="edit" disabled divider><UpgradeIcon/><Gap5/>{t("Edit Template...")}</MenuItem>,
                            isPublished && <MenuItem key="unpublish" disabled divider><UpgradeIcon/><Gap5/>{t("Unpublish Template")}</MenuItem>,
                            !isPublished && <MenuItem key="publish" disabled divider><UpgradeIcon/><Gap5/>{t("Publish Template")}</MenuItem>,
                        ] : [
                            <MenuItem key="settings" divider onClick={handleEditTemplateSettings}><Icon>label</Icon>{t("Template Settings...")}</MenuItem>,
                            <MenuItem key="edit" divider onClick={handleEditItem}><Icon>edit</Icon>{t("Edit Template...")}</MenuItem>,
                            isPublished && <MenuItem key="unpublish" divider onClick={handleUnpublish}><Icon>unpublished</Icon>{t("Unpublish Template")}</MenuItem>,
                            !isPublished && <MenuItem key="publish" divider onClick={handlePublish}><Icon>published_with_changes</Icon>{t("Publish Template")}</MenuItem>,
                        ].filter(Boolean)}
                        <MenuItem key="delete" onClick={handleDeleteItem}><Icon>delete</Icon>{t("Delete Template")}</MenuItem>
                    </IconMenu>
                }
            </FlexBox>
        </FlexBox>
        <SlideThumbnailsContainer>
            <LoadableAsync
                load={() => fetchTemplateItem()}
                renderWhileLoading={() => {
                    return (
                        <SlideThumbnails style={canUpgradeToManageSharedResources ? { opacity: 0.5, cursor: "not-allowed" } : {}}>
                            {Array.from({ length: slideCount }, (_, i) => (
                                <EmptySlideThumbnail key={i}/>
                            ))}
                        </SlideThumbnails>
                    );
                }}
                renderChildren={() => {
                    if (error) {
                        return (
                            <StyledTemplateItem
                                key={baseTemplateItem.id}
                            >
                                <div>{t("Error loading template")}</div>
                            </StyledTemplateItem>
                        );
                    }

                    return (
                        <SlideThumbnails style={canUpgradeToManageSharedResources ? { opacity: 0.5, cursor: "not-allowed" } : {}}>
                            {
                                // @ts-ignore
                                loadedState.sips.map(slideId => <SlideThumbnail key={slideId} slideId={slideId}/>)
                            }
                        </SlideThumbnails>
                    );
                }}/>

        </SlideThumbnailsContainer>
    </StyledTemplateItem>);
};
